'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { Icon, IconComponent } from '@graftcode/design-system'
import * as Dialog from '@radix-ui/react-dialog'

import { useGraftcodeOnboardingStore } from '@apps/graftcode-onboarding/stores/graftcode-onboarding.store'

import { GRAFTCODE_ONBOARDING_ASSETS_BASE } from '../../graftcode-onboarding.const'
import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'
import type { OnboardingTutorialVideo } from '../../graftcode-onboarding-videos.const'
import { buildOnboardingYoutubeEmbedSrc, youtubeThumbnailUrl } from '../../graftcode-onboarding-videos.const'

import styles from './GraftcodeOnboardingDashboard.module.scss'

type GraftcodeOnboardingTutorialVideoProps = {
  video: OnboardingTutorialVideo | null
  title: string
}

export function GraftcodeOnboardingTutorialVideo({ video, title }: GraftcodeOnboardingTutorialVideoProps) {
  const t = useTranslations('graftcodeOnboarding')
  const tutorialVideoHidden = useGraftcodeOnboardingStore((state) => state.tutorialVideoHidden)
  const tutorialVideoPlaying = useGraftcodeOnboardingStore((state) => state.tutorialVideoPlaying)
  const dismissTutorialVideo = useGraftcodeOnboardingStore((state) => state.dismissTutorialVideo)
  const setTutorialVideoPlaying = useGraftcodeOnboardingStore((state) => state.setTutorialVideoPlaying)

  const [videoControlsVisible, setVideoControlsVisible] = useState(false)

  const openModal = useCallback(() => {
    setVideoControlsVisible(false)
    setTutorialVideoPlaying(true)
  }, [setTutorialVideoPlaying])

  const closeModal = useCallback(() => {
    setTutorialVideoPlaying(false)
    setVideoControlsVisible(false)
  }, [setTutorialVideoPlaying])

  useEffect(() => {
    if (!tutorialVideoPlaying) {
      setVideoControlsVisible(false)
    }
  }, [tutorialVideoPlaying])

  const modalEmbedSrc = useMemo(() => {
    if (!video) {
      return ''
    }
    return buildOnboardingYoutubeEmbedSrc(video.embedUrl, {
      autoplay: true,
      controls: videoControlsVisible,
    })
  }, [video, videoControlsVisible])

  const revealVideoControls = useCallback(() => {
    setVideoControlsVisible(true)
  }, [])

  if (!video) {
    return null
  }

  return (
    <>
      {!tutorialVideoHidden ? (
        <aside
          className={styles.videoPip}
          aria-label={t('dashboard.tutorialVideoLabel', { title })}
          data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.tutorialVideoPip}
        >
          <div className={styles.videoPipHead}>
            <p className={styles.videoPipTitle}>{title}</p>
            <button
              type="button"
              className={styles.videoPipClose}
              aria-label={t('dashboard.closeTutorialVideo')}
              onClick={dismissTutorialVideo}
            >
              <IconComponent icon={Icon.DismissRegular} size={16} />
            </button>
          </div>
          <div className={styles.videoPipMedia}>
            <button
              type="button"
              className={styles.videoPipPoster}
              aria-label={t('dashboard.playTutorialVideo')}
              onClick={openModal}
            >
              <Image
                className={styles.videoPipThumb}
                src={youtubeThumbnailUrl(video.youtubeId)}
                alt=""
                width={248}
                height={139}
              />
              <Image
                className={styles.videoPipPlayBadge}
                src={`${GRAFTCODE_ONBOARDING_ASSETS_BASE}/video-play-badge.svg`}
                alt=""
                width={32}
                height={32}
              />
            </button>
          </div>
        </aside>
      ) : null}

      <Dialog.Root open={tutorialVideoPlaying} onOpenChange={(open) => !open && closeModal()}>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.videoModalOverlay} />
          <Dialog.Content
            className={styles.videoModalSheet}
            aria-describedby={undefined}
            data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.tutorialVideoModal}
          >
            <header className={styles.videoModalHeader}>
              <Dialog.Title className={styles.videoModalTitle}>{title}</Dialog.Title>
              <Dialog.Close
                type="button"
                className={styles.videoModalClose}
                aria-label={t('dashboard.closeTutorialVideo')}
              >
                <IconComponent icon={Icon.DismissRegular} size={24} />
              </Dialog.Close>
            </header>
            <div className={styles.videoModalBody} onPointerEnter={revealVideoControls} onClick={revealVideoControls}>
              <iframe
                key={videoControlsVisible ? 'with-controls' : 'chromeless'}
                className={styles.videoModalFrame}
                src={modalEmbedSrc}
                title={title}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}
